import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Circle,
  Clock,
  CreditCard,
  FileText,
  Shield,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Task } from '@/services/types/client-intake-types';
import {
  CanonicalTaskGroup,
  CanonicalTaskStatus,
} from '@/services/types/task-types';

interface Day1TaskGroup {
  name: string;
  canonicalGroup: CanonicalTaskGroup;
  tasks: Task[];
  icon: React.ElementType;
  color: {
    primary: string;
    secondary: string;
    completed: string;
    pending: string;
    inProgress: string;
  };
}

interface Day1TaskGroupsProps {
  tasks: Task[];
  onTaskClick: (taskId: string) => void;
  isDay1Task?: (task: Task) => boolean;
  qualifyingGroups?: CanonicalTaskGroup[];
}

const taskGroupConfigs: Omit<Day1TaskGroup, 'tasks'>[] = [
  {
    name: 'Intake',
    canonicalGroup: CanonicalTaskGroup.INTAKE,
    icon: FileText,
    color: {
      primary: 'text-indigo-600',
      secondary: 'text-indigo-500',
      completed: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      pending: 'bg-orange-50 text-orange-700 border-orange-200',
      inProgress: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    },
  },
  {
    name: 'Initial Review',
    canonicalGroup: CanonicalTaskGroup.INITIAL_REVIEW,
    icon: FileText,
    color: {
      primary: 'text-blue-600',
      secondary: 'text-blue-500',
      completed: 'bg-blue-50 text-blue-700 border-blue-200',
      pending: 'bg-orange-50 text-orange-700 border-orange-200',
      inProgress: 'bg-blue-50 text-blue-700 border-blue-200',
    },
  },
  {
    name: 'Police & Investigation',
    canonicalGroup: CanonicalTaskGroup.POLICE_REPORT_INVESTIGATION,
    icon: Shield,
    color: {
      primary: 'text-purple-600',
      secondary: 'text-purple-500',
      completed: 'bg-purple-50 text-purple-700 border-purple-200',
      pending: 'bg-orange-50 text-orange-700 border-orange-200',
      inProgress: 'bg-purple-50 text-purple-700 border-purple-200',
    },
  },
  {
    name: '1P Insurance',
    canonicalGroup: CanonicalTaskGroup.FIRST_PARTY_INSURANCE,
    icon: CreditCard,
    color: {
      primary: 'text-emerald-600',
      secondary: 'text-emerald-500',
      completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      pending: 'bg-orange-50 text-orange-700 border-orange-200',
      inProgress: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    },
  },
  {
    name: '3P Insurance',
    canonicalGroup: CanonicalTaskGroup.THIRD_PARTY_INSURANCE,
    icon: Building2,
    color: {
      primary: 'text-cyan-600',
      secondary: 'text-cyan-500',
      completed: 'bg-cyan-50 text-cyan-700 border-cyan-200',
      pending: 'bg-orange-50 text-orange-700 border-orange-200',
      inProgress: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    },
  },
];

const getTaskStatusIcon = (status: CanonicalTaskStatus) => {
  switch (status) {
    case CanonicalTaskStatus.COMPLETED:
      return <CheckCircle2 className="h-4 w-4 text-green-600" />;
    case CanonicalTaskStatus.IN_PROGRESS:
      return <Clock className="h-4 w-4 text-blue-600" />;
    case CanonicalTaskStatus.BLOCKED:
      return <AlertTriangle className="h-4 w-4 text-red-600" />;
    default:
      return <Circle className="h-4 w-4 text-gray-400" />;
  }
};

const getTaskStatusText = (status: CanonicalTaskStatus) => {
  switch (status) {
    case CanonicalTaskStatus.COMPLETED:
      return 'Completed';
    case CanonicalTaskStatus.IN_PROGRESS:
      return 'In Progress';
    case CanonicalTaskStatus.BLOCKED:
      return 'Blocked';
    case CanonicalTaskStatus.PENDING:
      return 'Pending';
    default:
      return 'Pending';
  }
};

const getGroupStatus = (tasks: Task[]) => {
  const completed = tasks.filter(
    (t) => t.status.canonical_status === CanonicalTaskStatus.COMPLETED,
  ).length;
  const total = tasks.length;

  if (total === 0)
    return { label: 'No tasks', ratio: '(0/0)', isComplete: false };
  if (completed === total)
    return {
      label: 'Complete',
      ratio: `(${completed}/${total})`,
      isComplete: true,
    };

  return {
    label: 'Pending',
    ratio: `(${completed}/${total})`,
    isComplete: false,
  };
};

const getTaskUpdateInfo = (task: Task) => {
  const updates = task.updates || [];
  const updateCount = updates.length;

  if (updateCount === 0) {
    return { count: 0, summary: 'No updates' };
  }

  const lastUpdate = [...updates].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )?.[0];

  if (!lastUpdate) {
    return {
      count: updateCount,
      summary: `${updateCount} update${updateCount !== 1 ? 's' : ''}`,
    };
  }

  const lastUpdateDate = new Date(lastUpdate.created_at);
  const now = new Date();
  const diffMs = now.getTime() - lastUpdateDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  let timeAgo = '';
  if (diffDays > 0) timeAgo = `${diffDays}d ago`;
  else if (diffHours > 0) timeAgo = `${diffHours}h ago`;
  else if (diffMinutes > 0) timeAgo = `${diffMinutes}m ago`;
  else timeAgo = 'Just now';

  const authorName =
    lastUpdate.user?.display_name || lastUpdate.user?.email || 'System';

  return {
    count: updateCount,
    summary: `${updateCount} update${updateCount !== 1 ? 's' : ''}`,
    lastUpdate: {
      author: authorName,
      timeAgo,
    },
  };
};

export const Day1TaskGroups: React.FC<Day1TaskGroupsProps> = ({
  tasks,
  onTaskClick,
  isDay1Task,
  qualifyingGroups = [],
}) => {
  // Initialize with all groups expanded by default
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  // Group tasks by canonical group
  const taskGroups = useMemo(() => {
    const groups: Day1TaskGroup[] = taskGroupConfigs.map((config) => ({
      ...config,
      tasks: tasks.filter(
        (task) =>
          task.group?.canonical_group === config.canonicalGroup &&
          (isDay1Task ? isDay1Task(task) : true),
      ),
    }));

    // Only return groups that have tasks
    return groups.filter((group) => group.tasks.length > 0);
  }, [tasks, isDay1Task]);

  // Initialize all groups as expanded on first render only
  React.useEffect(() => {
    const initialOpenState: Record<string, boolean> = {};
    taskGroups.forEach((group) => {
      initialOpenState[group.name] = true; // Default to expanded
    });
    setOpenGroups((prev) => {
      // Only initialize if we don't have any existing state
      if (Object.keys(prev).length === 0) {
        return initialOpenState;
      }
      // For existing groups, preserve their state; for new groups, default to expanded
      const newState = { ...prev };
      taskGroups.forEach((group) => {
        if (!(group.name in newState)) {
          newState[group.name] = true;
        }
      });
      return newState;
    });
  }, [taskGroups]);

  const toggleGroup = (groupName: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const handleTaskClick = (taskId: string) => {
    onTaskClick(taskId);
  };

  const handleTaskKeyDown = (event: React.KeyboardEvent, taskId: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleTaskClick(taskId);
    }
  };

  const activeGroupsCount = taskGroups.length;

  if (taskGroups.length === 0) {
    return (
      <Card className="p-6 text-center">
        <div className="text-gray-500">
          <p className="text-sm">No Day 1 tasks found for this matter.</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          Task Groups ({activeGroupsCount} active)
        </h2>
      </div>

      {/* Task Groups */}
      <div className="space-y-1 bg-white rounded-lg border shadow-sm overflow-hidden">
        {taskGroups.map((group) => {
          const isOpen = openGroups[group.name] ?? true; // Default to open if not set
          const { label, ratio, isComplete } = getGroupStatus(group.tasks);
          const Icon = group.icon;
          const isQualifyingGroup = qualifyingGroups.includes(
            group.canonicalGroup,
          );

          return (
            <div
              key={group.name}
              className={cn(
                'border-b border-gray-100 last:border-b-0',
                isQualifyingGroup && 'bg-yellow-50 ring-1 ring-yellow-200',
              )}
            >
              {/* Group Header */}
              <button
                type="button"
                onClick={() => toggleGroup(group.name)}
                className={cn(
                  'w-full flex items-center justify-between p-4 text-left transition-colors',
                  isQualifyingGroup
                    ? 'hover:bg-yellow-100'
                    : 'hover:bg-gray-50',
                )}
              >
                <div className="flex items-center gap-3">
                  {/* Expand/Collapse Icon */}
                  {isOpen ? (
                    <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                  )}

                  {/* Group Icon */}
                  <Icon className={cn('h-5 w-5', group.color.primary)} />

                  {/* Group Name */}
                  <span className="font-medium text-gray-900">
                    {group.name}
                  </span>

                  {/* Day 1 Qualifier Badge */}
                  {isQualifyingGroup && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-200 text-yellow-800">
                      Day 1
                    </span>
                  )}

                  {/* Status Badge */}
                  <span
                    className={cn(
                      'px-2 py-1 rounded-full text-xs font-medium',
                      isComplete
                        ? 'bg-green-100 text-green-800'
                        : 'bg-orange-100 text-orange-800',
                    )}
                  >
                    {label}
                  </span>
                </div>

                {/* Task Count */}
                <span className="text-sm text-gray-500 font-medium">
                  {ratio}
                </span>
              </button>

              {/* Task List */}
              {isOpen && (
                <div
                  className={cn(
                    'border-t border-gray-100',
                    isQualifyingGroup ? 'bg-yellow-50' : 'bg-gray-50',
                  )}
                >
                  {group.tasks.map((task) => {
                    const updateInfo = getTaskUpdateInfo(task);
                    const taskStatusClass = (() => {
                      if (
                        task.status.canonical_status === CanonicalTaskStatus.COMPLETED
                      ) {
                        return group.color.completed;
                      }
                      if (
                        task.status.canonical_status ===
                        CanonicalTaskStatus.IN_PROGRESS
                      ) {
                        return group.color.inProgress;
                      }
                      return group.color.pending;
                    })();

                    return (
                      <button
                        key={task.id}
                        type="button"
                        className={cn(
                          'w-full flex items-start justify-between p-4 text-left cursor-pointer transition-colors border-b border-gray-100 last:border-b-0',
                          isQualifyingGroup
                            ? 'hover:bg-yellow-100'
                            : 'hover:bg-white',
                        )}
                        onClick={() => handleTaskClick(task.id)}
                        onKeyDown={(e) => handleTaskKeyDown(e, task.id)}
                        aria-label={`Open task: ${task.name}`}
                      >
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          {/* Task Status Icon */}
                          <div className="mt-0.5">
                            {getTaskStatusIcon(task.status.canonical_status)}
                          </div>

                          <div className="flex-1 min-w-0">
                            {/* Task Name */}
                            <div
                              className={cn(
                                'text-sm font-medium mb-1',
                                task.status.canonical_status ===
                                  CanonicalTaskStatus.COMPLETED
                                  ? 'text-gray-400 line-through'
                                  : 'text-gray-900',
                              )}
                            >
                              {task.name}
                            </div>

                            {/* Updates Info */}
                            <div className="text-xs text-gray-500">
                              <div className="font-medium text-gray-700">
                                {updateInfo.summary}
                              </div>
                              {updateInfo.lastUpdate && (
                                <div
                                  className="truncate"
                                  title={`Last updated by ${updateInfo.lastUpdate.author}`}
                                >
                                  {updateInfo.lastUpdate.author} •{' '}
                                  {updateInfo.lastUpdate.timeAgo}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Task Status */}
                        <span
                          className={cn(
                            'text-xs px-2 py-1 rounded-full font-medium flex-shrink-0 mt-0.5',
                            taskStatusClass,
                          )}
                        >
                          {getTaskStatusText(task.status.canonical_status)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="text-center">
        <p className="text-sm text-gray-500">
          {activeGroupsCount} task{' '}
          {activeGroupsCount === 1 ? 'group' : 'groups'} with Day 1 tasks
        </p>
      </div>
    </div>
  );
};
